import classNames from 'classnames'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useMatch, useNavigate } from 'react-router'
import { useMediaQuery } from 'usehooks-ts'


import { CabinetLink, useAuth, useAuthFlow } from '@features/auth'
import { CartLink } from '@features/cart'
import { DeliveryInfo } from '@features/delivery'
import { FavoriteLink } from '@features/favorite'
import { Burger } from '@features/mobile-menu'
import useModalStore from '@features/modal/model'
import { SearchInput } from '@features/search'
import SwitchLocale from '@features/switch-locale/ui'

import { ModalBonus } from '@widgets/modal-bonus'
import { ModalCategories } from '@widgets/modal-categories'

import {
  NAVIGATION,
  URL_CUSTOMER_LOYALTY,
  URL_CUSTOMER_ORDERS,
  URL_CUSTOMER_PROFILE,
} from '@shared/contants'
import { useMyBonusTotalQuery } from '@shared/generated'
import { cn } from '@shared/lib/cn'
import Button from '@shared/ui/button'
import Container from '@shared/ui/container'
import Logo from '@shared/ui/logo'
import Space from '@shared/ui/space'

import { TabBar } from './TabBar'

import styles from './styles.module.scss'



export default function Header () {
  const { t: tn } = useTranslation('navigation')
  const { t: tp } = useTranslation('personal')
  const isTablet = useMediaQuery('(max-width: 1179.9px)')
  const isShowTabbar = useMediaQuery('(max-width: 1179.9px)')
  const { openModal } = useModalStore()
  const { user, requireRegistration, isAuthorized } = useAuth()
  const { data } = useMyBonusTotalQuery()
  const navigate = useNavigate()

  const match = useMatch('/product/:slug')
  const isDetailProductPage = !!match?.params.slug?.length

  const activeCardNumber = data?.myBonus?.bonusCards?.find(
    (card) => card?.status === 'active',
  )?.cardNumber
  const auhtFlowOrders = useAuthFlow(() => {
    navigate(`${URL_CUSTOMER_PROFILE}/${URL_CUSTOMER_ORDERS}`)
  })
  const authFlow = useAuthFlow(() =>
    navigate(`${URL_CUSTOMER_PROFILE}/${URL_CUSTOMER_LOYALTY}`),
  )

  const COUNT = data?.myBonus?.activeBonuses ?? 0

  const COUNT_FORMATTED = useMemo(() => COUNT.toLocaleString('ru-RU'), [ COUNT ])

  return (
    <>
      <header
        className={cn(styles.header, {
          [styles.auth]: !!user,
        })}
      >
        <Container>
          <Space className={styles.header__inner}>
            <div className={styles.header__grid}>
              <Logo className={styles.header__logo} />

              <DeliveryInfo className={styles.header__delivery} />

              {!isTablet && (
                <div className={styles.header__nav}>
                  <nav>
                    <ul
                      className={cn(styles['header__nav-list'], 'reset-list')}
                    >
                      {NAVIGATION.map((i) => i.links)
                        .flat()
                        .concat()
                        .filter((i) => i.showHeader)
                        .map((item, index) => {
                          return (
                            <li
                              className={styles['header__nav-item']}
                              key={index}
                            >
                              <Link
                                className={styles['header__nav-link']}
                                to={item.url}
                              >
                                {tn(item.title)}
                              </Link>
                            </li>
                          )
                        })}
                    </ul>
                  </nav>

                  <SwitchLocale className={styles['header__switch-locale']} />
                </div>
              )}

              {!isTablet && (
                <Button
                  className={styles.header__catalog}
                  size="large"
                  onClick={() =>
                    openModal('categories', <ModalCategories />, {
                      hideIconClose: true,
                      disabledScroll: true,
                      classes: {
                        body: classNames(
                          styles['modal-body'],
                          styles['modal-body--categories'],
                        ),
                      },
                    })
                  }
                >
                  <img
                    src="/icons/grid.svg"
                    alt=""
                    loading="lazy"
                    fetchPriority="low"
                  />
                  {tn('catalog')}
                </Button>
              )}

              {/* {!isTablet && (
                <Button
                  className={styles.header__promotions}
                  to="/promotions"
                  theme="grey"
                  size="large"
                >
                  <img
                    src="/icons/percent.svg"
                    alt=""
                    loading="lazy"
                    fetchPriority="low"
                  />
                  {tn("promotions")}
                </Button>
              )} */}
              {/* временно скрыли ссылку на акции */}

              <SearchInput className={styles.header__search} />

              {!isTablet && user && (
                <FavoriteLink className={styles.header__favorite} />
              )}

              <CabinetLink className={styles.header__cabinet} />

              {user && !isTablet && (
                <Button
                  className={styles.header__orders}
                  theme="grey"
                  onClick={auhtFlowOrders}
                >
                  <img src="/icons/history.svg" />
                  {tn('orders')}
                </Button>
              )}

              {!isTablet && <CartLink className={styles.header__cart} />}

              {isTablet && <Burger />}

              {isTablet && isAuthorized && (
                <div className={styles.header__cards}>
                  <div
                    className={cn(styles.header__card, styles.bonus)}
                    onClick={() => {
                      if (!requireRegistration) {
                        openModal(
                          'bonus',
                          <ModalBonus card={activeCardNumber} />,
                          {
                            title: tp('bonus.modal'),
                          },
                        )
                        // зареган в ПЛ
                      }
                      else {
                        // не зареган в ПЛ
                        authFlow()
                      }
                    }}
                  >
                    <h3>{tp('payment.card')}</h3>

                    <div className={styles['header__card-bottom']}>
                      {!requireRegistration && (
                        <span className={styles['header__card-bonus-count']}>
                          {COUNT_FORMATTED}{' '}
                          {tp('bonusHistory.bonusesAbbreviated')}
                        </span>
                      )}

                      <img
                        className={styles['header__card-icon']}
                        src="/icons/barcode.svg"
                        alt=""
                      />
                    </div>
                  </div>
                  <Link
                    to={`${URL_CUSTOMER_PROFILE}/${URL_CUSTOMER_ORDERS}`}
                    className={styles.header__card}
                  >
                    <h3>{tn('orders')}</h3>{' '}
                    <div className={styles['header__card-bottom']}>
                      <img
                        className={styles['header__card-icon']}
                        src="/icons/shopping-cart.svg"
                        alt=""
                      />
                    </div>
                  </Link>

                  {/* <div className={styles.header__card}>
                    <h3>{tn("promotions")}</h3>{" "}
                    <div className={styles["header__card-bottom"]}>
                      <img
                        className={styles["header__card-icon"]}
                        src="/icons/percent.svg"
                        alt=""
                      />
                    </div>
                  </div> */}
                  {/* временно скрыли ссылку акции */}
                </div>
              )}
            </div>
          </Space>
        </Container>
      </header>
      {isShowTabbar && <TabBar withProductQuery={isDetailProductPage} />}
    </>
  )
}
